import { SubdirectoryArrowRightOutlined as ProductLinkArrow } from '@mui/icons-material';
import LoopIcon from '@mui/icons-material/Loop';
import { Alert, Button, Grid, styled, Typography } from '@mui/material';
import { isNil, set } from 'lodash';
import { forwardRef, useRef } from 'react';
import { Path } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { IconTypography } from 'src/components/iconTypography/IconTypography';
import { PATHS } from 'src/constants/paths';
import { useGetAllAssets } from 'src/hooks';
import type { IGcfProductListProductImage } from 'src/interfaces';
import { lang, theme } from 'src/utils';
import { getThumbnailSrc } from 'src/utils/gcf-helper';

import { NumberInputWidget } from '../numberInputWidget/NumberInputWidget';
import { ResourceSelectorWidget } from '../resourceSelectorWidget/ResourceSelectorWidget';
import { SwitchButtonWidget } from '../switchButtonWidget/SwitchButtonWidget';
import { ProductImageWidgetProps } from './ProductImageWidget.interface';
import {
  StyledSupportingProductInfo,
  StyledSupportingProductLinkWrapper,
} from './ProductImageWidget.style';

const ButtonContainer = styled(Grid)`
  display: flex;
  align-items: center;
  gap: 20px;
`;

export const ProductImageWidget = forwardRef<
  HTMLDivElement,
  ProductImageWidgetProps<IGcfProductListProductImage | undefined>
>((props, ref) => {
  const resourceSelectorRef = useRef<any>(null);
  const { data: assets = [] } = useGetAllAssets();
  const onFieldChange =
    (
      field: Path<
        ProductImageWidgetProps<IGcfProductListProductImage>['value']
      >,
    ) =>
    (value: unknown) => {
      const updatedValues = set(
        { ...props.value },
        field,
        value,
      ) as IGcfProductListProductImage;

      if (field === 'assetName' && isNil(value)) {
        updatedValues.visible = false;
      }

      props.onChange(updatedValues);
    };

  let supportingProductName = '';

  if (props.hasSupportingProduct && props.product?.product_code) {
    supportingProductName = props?.value?.assetName
      ? props.product?.productVariations.find(
          (item) => item.product_code === props.value?.productCode,
        )?.name || ''
      : props?.product.name_override || props?.product.product_name;
  }

  return (
    <Grid container spacing={2} ref={ref}>
      {!props.option?.hideProductLabel && (
        <Grid item xs={12}>
          <Typography fontWeight={400}>{lang.productImage}</Typography>
        </Grid>
      )}

      <Grid item xs={12}>
        <SwitchButtonWidget
          value={props.value?.visible}
          onChange={onFieldChange('visible')}
        />
      </Grid>

      {props.value?.visible && (
        <>
          <Grid item xs={12}>
            <ResourceSelectorWidget
              ref={resourceSelectorRef}
              showThemeSelector
              value={
                assets.find(
                  (asset) =>
                    asset.assetName ===
                    (props.value?.assetName || props.fallbackImgSrc),
                )?.thumbnailUrl ||
                getThumbnailSrc(props.value?.assetName || props.fallbackImgSrc)
              }
              productCode={props.product?.product_code}
              productImage={props.value}
              onChange={(value) =>
                props.onChange(value as IGcfProductListProductImage)
              }
              disabled={!props.option?.enableSelectProductImage}
              enableUploadProductImages={
                props.option?.enableUploadProductImages
              }
              hasSupportingProduct={props.hasSupportingProduct}
            />
          </Grid>

          {props.hasSupportingProduct && props.product?.product_code && (
            <Grid item xs={12}>
              <StyledSupportingProductLinkWrapper>
                <IconTypography
                  icon={<ProductLinkArrow />}
                  text={props.value.productCode || props.product?.product_code}
                  textFontSize="16px"
                  textColor={theme.palette.brand.charcoal}
                  iconSize="16px"
                  iconColor={theme.palette.brand.lightblack}
                />

                <StyledSupportingProductInfo>
                  <Link
                    target="_blank"
                    to={PATHS.PRODUCT_DETAILS(props.product?.product_code)}>
                    {supportingProductName}
                  </Link>
                </StyledSupportingProductInfo>
              </StyledSupportingProductLinkWrapper>
            </Grid>
          )}

          {props.option?.enableSelectProductImage && (
            <>
              <ButtonContainer item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<LoopIcon />}
                  onClick={() =>
                    resourceSelectorRef.current?.handleImageClick()
                  }>
                  {lang.replace}
                </Button>

                <Button
                  variant="outlined"
                  disabled={!props.value.assetName}
                  onClick={() => {
                    onFieldChange('assetName')('');
                    if (props.hasSupportingProduct)
                      onFieldChange('productCode')(props.product?.product_code);
                  }}>
                  {lang.resetToDefault}
                </Button>
              </ButtonContainer>

              <Grid item xs={12}>
                <Alert severity="info">
                  {lang.productImageAssociatedTheme}
                </Alert>
              </Grid>
            </>
          )}

          {props.option?.showProductImageController && (
            <>
              <Grid item xs={6}>
                <NumberInputWidget
                  label="Width"
                  value={props.value?.style?.width}
                  onChange={onFieldChange('style.width')}
                  options={{ suffix: 'px' }}
                />
              </Grid>

              <Grid item xs={6}>
                <NumberInputWidget
                  label="Height"
                  value={props.value?.style?.height}
                  onChange={onFieldChange('style.height')}
                  options={{ suffix: 'px' }}
                />
              </Grid>

              <Grid item xs={6}>
                <NumberInputWidget
                  label="X-axis"
                  value={props.value?.style?.left}
                  onChange={onFieldChange('style.left')}
                  options={{ suffix: 'px' }}
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInputWidget
                  label="Y-axis"
                  value={props.value?.style?.top}
                  onChange={onFieldChange('style.top')}
                  options={{ suffix: 'px' }}
                />
              </Grid>

              {props?.option?.showZIndex && (
                <Grid item xs={6}>
                  <NumberInputWidget
                    label="Z-index"
                    value={props.value?.style?.zIndex}
                    onChange={onFieldChange('style.zIndex')}
                  />
                </Grid>
              )}
            </>
          )}
        </>
      )}
    </Grid>
  );
});
